import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'

import { useUpdateUserMutation } from '@/app/services/user'
import { selectCurrentUserId, selectLanguage, selectLanguageList, setLanguage } from '@/app/slices/appSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'

// Only written by LanguageSelectorButton when the user explicitly selects a language.
// i18next-browser-languagedetector is configured with caches: [] to avoid writing to this key.
const LANGUAGE_STORAGE_KEY = 'webstore-language'

/**
 * Converts a 2-letter region code to a flag emoji using Unicode Regional Indicator Symbols.
 * @param {string} regionCode - ISO 3166-1 alpha-2 country code (e.g., "US", "GB")
 * @returns {string} Flag emoji (e.g., "🇺🇸", "🇬🇧")
 */
const regionToFlagEmoji = (regionCode) => {
    if (!regionCode || regionCode.length !== 2) return ''

    return String.fromCodePoint(
        ...regionCode
            .toUpperCase()
            .split('')
            .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65)
    )
}

/**
 * Formats a language entry from the language list for display.
 * @param {{ nativeName: string, region: string }} language - Language entry from Locize
 * @returns {string} Formatted name (e.g., "English (US)")
 */
const getLanguageFormattedName = (language) => {
    const nativeName = language.nativeName.split(',')[0]
    return `${nativeName[0].toUpperCase() + nativeName.slice(1)} (${language.region})`
}

/**
 * A toolbar button that opens a dropdown menu for selecting the display language.
 * Persists the selection to localStorage (all users) and to the DB (authenticated users).
 * @returns {React.ReactElement} Language selector icon button with dropdown menu
 */
const LanguageSelectorButton = () => {
    const { i18n, t } = useTranslation()
    const dispatch = useDispatch()

    const { isAuthenticated } = useKindeAuth()

    const languageList = useSelector(selectLanguageList)
    const currentLanguage = useSelector(selectLanguage)
    const organisationId = useSelector(selectOrganisationId)
    const userId = useSelector(selectCurrentUserId)

    const currentLanguageEntry = languageList[currentLanguage]

    const [updateUser] = useUpdateUserMutation()
    const [anchorEl, setAnchorEl] = useState(null)

    const open = Boolean(anchorEl)

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSelectLanguage = (languageCode) => {
        handleClose()

        localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode)
        i18n.changeLanguage(languageCode)
        dispatch(setLanguage(languageCode))

        if (isAuthenticated && organisationId && userId) {
            updateUser({
                organisationId,
                userId,
                user: { language: languageCode },
            })
                .unwrap()
                .catch((err) => console.error('Failed to save language preference', err))
        }
    }

    return (
        <>
            <IconButton
                aria-controls={open ? 'language-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                aria-label={t('Select language')}
                color="secondary"
                size="small"
                onClick={handleOpen}
            >
                <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>
                    {currentLanguageEntry ? regionToFlagEmoji(currentLanguageEntry.region) : '🌐'}
                </Typography>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                id="language-menu"
                open={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClose={handleClose}
            >
                {Object.entries(languageList).map(([code, language]) => (
                    <MenuItem
                        key={code}
                        selected={code === currentLanguage}
                        onClick={() => handleSelectLanguage(code)}
                    >
                        <ListItemIcon>
                            <Typography sx={{ fontSize: '1.25rem' }}>{regionToFlagEmoji(language.region)}</Typography>
                        </ListItemIcon>
                        <ListItemText>{getLanguageFormattedName(language)}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

export default LanguageSelectorButton
export { LANGUAGE_STORAGE_KEY }
